import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropertySVGOverlayV3 from './PropertySVGOverlayV3';
import { getResponsiveViewBox } from '../../../config/mapConfig';
import './InteractivePropertyMapV3.css';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP = 0.5;

function InteractivePropertyMapV3({
  properties = [],
  investmentData,
  selectedPropertyId,
  onPropertySelect,
  hideToolbar = false
}) {
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [currentArtboard, setCurrentArtboard] = useState(null);
  const [scale, setScale] = useState(1);
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    window.matchMedia('(max-width: 768px)').matches
  );
  const containerRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e) => setIsMobileViewport(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handlePropertyClick = useCallback((propertyId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (onPropertySelect) {
      onPropertySelect(propertyId);
    }
  }, [onPropertySelect]);

  const handlePropertyHover = useCallback((propertyId) => {
    setHoveredProperty(propertyId);
  }, []);

  const handleImageError = useCallback(() => {
    setImageError(true);
    console.error('Investment map image failed to load');
  }, []);

  const handleImageRetry = useCallback(() => {
    setImageError(false);
  }, []);

  const handleArtboardChange = useCallback((artboard) => {
    setCurrentArtboard(artboard);
    setImageError(false);
  }, []);

  // Responsive width tracking
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const zoomIn = () => setScale(prev => Math.min(MAX_SCALE, Math.round((prev + ZOOM_STEP) * 10) / 10));
  const zoomOut = () => setScale(prev => Math.max(MIN_SCALE, Math.round((prev - ZOOM_STEP) * 10) / 10));

  if (imageError) {
    return (
      <div className="interactive-property-map nav-v3">
        <div className="map-error">
          <div className="map-error-content">
            <p>Mapa inwestycji jest tymczasowo niedostępna</p>
            <button className="retry-button" onClick={handleImageRetry} type="button">
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  const navigatorConfig = investmentData?.navigatorConfigV2;
  const is360 = navigatorConfig?.is360 === true;

  if (!investmentData || !is360) {
    return null;
  }

  const mapImageUrl = (isMobileViewport && currentArtboard?.image?.urlMobile) ||
                     currentArtboard?.image?.url ||
                     investmentData.mapConfiguration?.imageUrl;

  const mapImageAlt = currentArtboard?.name ||
                     investmentData.mapConfiguration?.imageAlt ||
                     'Plan inwestycji';

  const viewBox = currentArtboard?.viewBox ||
               investmentData.mapConfiguration?.viewBox ||
               getResponsiveViewBox(containerWidth);

  const aspectRatio = currentArtboard?.image?.width && currentArtboard?.image?.height
    ? currentArtboard.image.width / currentArtboard.image.height
    : investmentData.mapConfiguration?.aspectRatio || 1.77;

  return (
    <>
    <div className="interactive-property-map nav-v3" ref={containerRef}>
      <div className="map-container">
        <div
          className="map-zoom-wrapper"
          style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}
        >
          <img
            src={mapImageUrl}
            alt={mapImageAlt}
            className="investment-image"
            style={{ aspectRatio: aspectRatio }}
            onError={handleImageError}
            loading="lazy"
          />

          <PropertySVGOverlayV3
            ref={overlayRef}
            navigatorConfig={navigatorConfig}
            properties={properties}
            selectedProperty={selectedPropertyId}
            hoveredProperty={hoveredProperty}
            onPropertyClick={handlePropertyClick}
            onPropertyHover={handlePropertyHover}
            onArtboardChange={handleArtboardChange}
            viewBox={viewBox}
            enableNavigatorTooltips={investmentData.enableNavigatorTooltips}
            overlayEnabled={overlayEnabled}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            canZoomIn={scale < MAX_SCALE}
            canZoomOut={scale > MIN_SCALE}
          />
        </div>
      </div>
    </div>

      {is360 && !hideToolbar && (
        <div className="nav-v3-toolbar">
          <div className="nav-v3-group">
            <button
              className="nav-v3-toolbar-btn"
              onClick={() => overlayRef.current?.rotateOnce(-1)}
              aria-label="Obróć w lewo"
            >
              <span className="nav-v3-toolbar-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="nav-v3-toolbar-label">Obróć w lewo</span>
            </button>
            <span className="nav-v3-group-label">Obróć</span>
            <button
              className="nav-v3-toolbar-btn"
              onClick={() => overlayRef.current?.rotateOnce(1)}
              aria-label="Obróć w prawo"
            >
              <span className="nav-v3-toolbar-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="nav-v3-toolbar-label">Obróć w prawo</span>
            </button>
          </div>
          <div className="nav-v3-group">
            <button
              className="nav-v3-toolbar-btn"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              aria-label="Przybliż"
            >
              <span className="nav-v3-toolbar-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="nav-v3-toolbar-label">Przybliż</span>
            </button>
            <span className="nav-v3-group-label">Zoom</span>
            <button
              className="nav-v3-toolbar-btn"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              aria-label="Oddal"
            >
              <span className="nav-v3-toolbar-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </span>
              <span className="nav-v3-toolbar-label">Oddal</span>
            </button>
          </div>
          <button
            className={`nav-v3-toolbar-btn nav-v3-toolbar-btn--3d ${overlayEnabled ? '' : 'nav-v3-toolbar-btn--off'}`}
            onClick={() => setOverlayEnabled(v => !v)}
            aria-label={overlayEnabled ? 'Ukryj obrysy 3D' : 'Pokaż obrysy 3D'}
          >
            <span className="nav-v3-mobile-label">Obrysy</span>
            <span className="nav-v3-toolbar-icon nav-v3-3d-icon">
              <span className="nav-v3-3d-text">3D</span>
              {!overlayEnabled && <span className="nav-v3-3d-strike" aria-hidden="true" />}
            </span>
            <span className="nav-v3-toolbar-label">{overlayEnabled ? 'Ukryj obrysy 3D' : 'Pokaż obrysy 3D'}</span>
          </button>
        </div>
      )}
    </>
  );
}

export default InteractivePropertyMapV3;
